import { useEffect } from "react";
import styled from "styled-components";
import { colors } from "../colors";
import icon from "../assets/skipgive-icon.png";
import { Image } from "../components/Reusable";
import { useViewport } from "../hooks/useViewport";

const Container = styled.div<{ $isDesktop: boolean }>`
  font-family: "Helvetica Neue", sans-serif;
  margin: 1.25rem;
  margin: 0 auto;
  padding: ${(props) => (props.$isDesktop ? "3rem 12rem" : "3rem 2rem")};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${colors.black};
`;

const SubTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 500;
  color: ${colors.black};
  margin-top: 1.25rem;
  text-transform: uppercase;
`;

const Paragraph = styled.p`
  font-size: 1.25rem;
  color: ${colors.black};
`;

const Strong = styled.strong`
  font-weight: bold;
`;

export const SmsPolicy = () => {
  const { isDesktop } = useViewport();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Container $isDesktop={isDesktop}>
      <Image src={icon} />
      <Title>SMS TEXT MESSAGING POLICY</Title>
      <Paragraph>Effective Date: October 23, 2025</Paragraph>
      <Paragraph>
        This SMS Text Messaging Policy ("SMS Policy") governs your use of
        SkipGive's text messaging services (the "SMS Services"). By using the SMS
        Services, you acknowledge and agree to the terms of this SMS Policy, our
        Terms of Service, and our Privacy Policy, all of which are incorporated
        herein by reference. PLEASE READ THIS POLICY CAREFULLY BEFORE USING THE
        SMS SERVICES.
      </Paragraph>

      <SubTitle>1. EXPRESS WRITTEN CONSENT AND TCPA COMPLIANCE</SubTitle>
      <Paragraph>
        <Strong>1.1 Your Express Consent:</Strong> By opting into SkipGive's SMS
        Services, providing your mobile phone number, and/or sending a text
        message to +1(310) 896-4373 or other phone numbers used by SkipGive (the
        "SkipGive Numbers"), you expressly consent to receive autodialed,
        automated, prerecorded, and/or artificial voice text messages and calls
        from or on behalf of SkipGive, your campaigns, and your authorized
        contact lists at the mobile phone number you provided. This consent
        applies even if your mobile number is registered on any state or federal
        Do Not Call list. Message frequency varies depending on your use of the
        Services and may include transactional messages, donation notifications,
        campaign updates, service announcements, and promotional content.
      </Paragraph>
      <Paragraph>
        <Strong>1.2 TCPA Compliance:</Strong> SkipGive complies with the
        Telephone Consumer Protection Act (TCPA), the Cellular Telecommunications
        Industry Association (CTIA) guidelines, and all other applicable federal
        and state laws and regulations governing text message communications. You
        acknowledge that consent to receive text messages is not a condition of
        purchase or use of SkipGive's platform, except where the SMS Services are
        an integral feature of the service you are requesting.
      </Paragraph>
      <Paragraph>
        <Strong>1.3 Contact List Consent:</Strong> If you use the SMS Services to
        send messages to your contact lists, you represent and warrant that: (a)
        you have obtained prior express written consent from each recipient to
        receive text messages from you through SkipGive's platform; (b) you
        maintain documentation of all such consents; (c) each recipient has
        affirmatively consented to receive autodialed or prerecorded messages;
        and (d) you will immediately cease sending messages to any recipient who
        requests to opt out or revokes their consent. You are solely responsible
        for obtaining, maintaining, and proving such consents, and for any
        violations of the TCPA or other applicable laws resulting from your
        failure to do so.
      </Paragraph>
      <Paragraph>
        <Strong>1.4 Revocation of Consent:</Strong> You understand and agree that
        you are not required to consent to receive promotional or non-essential
        text messages as a condition of using SkipGive's platform. You may
        revoke your consent to receive text messages at any time by following the
        opt-out procedures described in Section 7 below.
      </Paragraph>

      <SubTitle>2. MESSAGE FREQUENCY, CONTENT, AND TYPES</SubTitle>
      <Paragraph>
        <Strong>2.1 Message Frequency:</Strong> The number of messages you
        receive will vary depending on your interaction with the Platform and
        your campaign activities. Message frequency may range from occasional to
        multiple messages per day during active campaigns. You acknowledge that
        message frequency is not fixed and may increase or decrease based on your
        use of the SMS Services.
      </Paragraph>
      <Paragraph>
        <Strong>2.2 Message Types:</Strong> Messages may include, but are not
        limited to: (a) transactional messages related to your account,
        campaigns, or donations; (b) operational messages regarding system
        updates, maintenance, or changes to the Services; (c) donation
        confirmations and thank-you messages; (d) campaign updates and progress
        notifications; (e) customer service and support messages; and (f)
        promotional and marketing messages, which you may opt out of separately
        as described in Section 7.
      </Paragraph>
      <Paragraph>
        <Strong>2.3 Message Content:</Strong> SkipGive reserves the right to
        determine the content, format, and timing of all messages sent through
        the SMS Services in its sole discretion, subject to applicable law. While
        we strive to provide accurate and timely information, we do not guarantee
        the accuracy, completeness, or usefulness of any message content.
      </Paragraph>

      <SubTitle>3. MESSAGE AND DATA RATES; SUPPORTED CARRIERS</SubTitle>
      <Paragraph>
        <Strong>3.1 Message and Data Rates:</Strong> Message and data rates may
        apply for each text message sent or received in connection with the SMS
        Services. The specific rates depend on your mobile service provider and
        your wireless service plan. You are solely responsible for all charges
        associated with text messaging imposed by your mobile service provider.
        SkipGive is not responsible for any charges or fees imposed by your
        carrier.
      </Paragraph>
      <Paragraph>
        <Strong>3.2 Supported Carriers:</Strong> The SMS Services are available
        to subscribers of participating carriers in the United States, including
        but not limited to AT&amp;T, T-Mobile®, Verizon Wireless, Sprint, Boost,
        U.S. Cellular®, MetroPCS®, Cricket, Virgin Mobile, and other carriers.
        Carrier coverage and service quality may vary. Not all mobile devices or
        carriers may be supported. Check with your carrier for details about your
        specific wireless plan and any applicable rates.
      </Paragraph>
      <Paragraph>
        <Strong>3.3 Carrier Liability:</Strong> Participating carriers, including
        T-Mobile®, AT&amp;T, Verizon Wireless, and others, are not liable for
        delayed or undelivered messages. We are not responsible for any carrier
        delays, failures, or outages affecting message delivery.
      </Paragraph>

      <SubTitle>4. PRIVACY AND DATA PROTECTION</SubTitle>
      <Paragraph>
        <Strong>4.1 Information Collection:</Strong> When you use the SMS
        Services, we collect certain information, including your mobile phone
        number, carrier information, the date and time of messages, message
        content, and other information you provide. This information is used to
        provide the SMS Services, communicate with you and your contact lists,
        improve our Services, and comply with legal obligations.
      </Paragraph>
      <Paragraph>
        <Strong>4.2 Privacy Policy:</Strong> Our collection, use, and disclosure
        of your information is governed by our Privacy Policy, which is
        incorporated into this SMS Policy by reference. By using the SMS
        Services, you consent to the data practices described in our Privacy
        Policy. Please review our Privacy Policy at [privacy policy link] to
        understand how we handle your information.
      </Paragraph>
      <Paragraph>
        <Strong>4.3 Data Retention:</Strong> We retain SMS-related data for as
        long as necessary to provide the SMS Services, comply with legal
        obligations, resolve disputes, and enforce our agreements. You may
        request deletion of your data as described in our Privacy Policy, subject
        to applicable legal requirements and legitimate business needs.
      </Paragraph>
      <Paragraph>
        <Strong>4.4 Data Security:</Strong> We implement reasonable security
        measures to protect your information. However, no method of transmission
        or storage is completely secure, and we cannot guarantee absolute
        security. You acknowledge and accept the inherent security risks of
        providing information online and through SMS communications.
      </Paragraph>

      <SubTitle>5. DISCLAIMER OF WARRANTIES</SubTitle>
      <Paragraph>
        THE SMS SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
        WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST
        EXTENT PERMISSIBLE PURSUANT TO APPLICABLE LAW, SKIPGIVE DISCLAIMS ALL
        WARRANTIES, EXPRESS OR IMPLIED, STATUTORY OR OTHERWISE, INCLUDING BUT NOT
        LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, AND THOSE ARISING FROM A
        COURSE OF DEALING OR USAGE OF TRADE.
      </Paragraph>
      <Paragraph>
        SKIPGIVE DOES NOT WARRANT THAT: (A) THE SMS SERVICES WILL BE
        UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE; (B) MESSAGES WILL BE
        DELIVERED PROMPTLY OR AT ALL; (C) THE QUALITY OF THE SMS SERVICES WILL
        MEET YOUR EXPECTATIONS; OR (D) ANY ERRORS IN THE SMS SERVICES WILL BE
        CORRECTED. WE ARE NOT LIABLE FOR ANY DELAYS IN THE RECEIPT OF ANY SMS
        MESSAGES, AS DELIVERY IS SUBJECT TO EFFECTIVE TRANSMISSION FROM YOUR
        MOBILE SERVICE PROVIDER AND OTHER FACTORS BEYOND OUR CONTROL.
      </Paragraph>
      <Paragraph>
        YOU ACKNOWLEDGE THAT SMS MESSAGING HAS INHERENT LIMITATIONS AND MAY BE
        SUBJECT TO DELAYS, FAILURES, INTERRUPTIONS, INTERCEPTION, OR OTHER
        PROBLEMS INHERENT IN THE USE OF SUCH COMMUNICATIONS. WE ARE NOT
        RESPONSIBLE FOR ANY DELAYS, DELIVERY FAILURES, OR OTHER DAMAGE RESULTING
        FROM SUCH PROBLEMS.
      </Paragraph>

      <SubTitle>6. LIMITATION OF LIABILITY</SubTitle>
      <Paragraph>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
        SKIPGIVE, ITS AFFILIATES, LICENSORS, SERVICE PROVIDERS, EMPLOYEES,
        AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
        SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF
        PROFITS, REVENUES, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES,
        ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE SMS
        SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING
        NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, AND WHETHER OR NOT
        SKIPGIVE HAS BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE.
      </Paragraph>
      <Paragraph>
        IN NO EVENT SHALL THE AGGREGATE LIABILITY OF SKIPGIVE EXCEED FIFTY U.S.
        DOLLARS ($50.00). SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR
        LIMITATION OF CERTAIN WARRANTIES OR THE LIMITATION OR EXCLUSION OF
        LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, SOME OF
        THE LIMITATIONS SET FORTH ABOVE MAY NOT APPLY TO YOU.
      </Paragraph>

      <SubTitle>7. OPT-OUT AND UNSUBSCRIBE PROCEDURES</SubTitle>
      <Paragraph>
        <Strong>7.1 How to Opt Out:</Strong> You may opt out of receiving text
        messages at any time by texting "STOP," "END," "CANCEL," "UNSUBSCRIBE,"
        or "QUIT" to any SkipGive Number from which you receive messages. You may
        also contact us at hello@skipgive.com to request removal from our SMS
        lists. Upon receipt of your opt-out request, we will send you a
        confirmation message and cease sending messages to your mobile number,
        except as required for transactional or legally mandated communications.
      </Paragraph>
      <Paragraph>
        <Strong>7.2 Effect of Opt-Out:</Strong> After you opt out, you will no
        longer receive marketing or promotional text messages from SkipGive.
        However, you may continue to receive transactional messages necessary to
        provide services you have requested, such as donation confirmations,
        account notifications, or security alerts. To completely cease all
        messages, you may need to discontinue use of the Platform or close your
        account.
      </Paragraph>
      <Paragraph>
        <Strong>7.3 Opt-Out for Contact Lists:</Strong> If you are a recipient on
        a user's contact list and wish to opt out, reply "STOP" to the message or
        contact the campaign organizer directly. We will process opt-out requests
        promptly and will not be responsible for messages sent before the opt-out
        request is fully processed.
      </Paragraph>
      <Paragraph>
        <Strong>7.4 Re-Subscribing:</Strong> If you opt out and later wish to
        re-subscribe, you may text "START" or "UNSTOP" to the SkipGive Number or
        re-enable SMS notifications through your account settings on the
        Platform.
      </Paragraph>

      <SubTitle>8. CUSTOMER SUPPORT</SubTitle>
      <Paragraph>
        For help with the SMS Services, you may: (a) text "HELP" to any SkipGive
        Number; (b) email our support team at hello@skipgive.com; or (c) visit
        our help center on the Platform. When you text "HELP," you will receive a
        message with instructions and support contact information. Our customer
        support team is available to assist with questions about the SMS
        Services, opt-out procedures, and technical issues.
      </Paragraph>

      <SubTitle>9. INDEMNIFICATION</SubTitle>
      <Paragraph>
        You agree to defend, indemnify, and hold harmless SkipGive and its
        affiliates, directors, officers, employees, agents, licensors, and
        service providers from and against any and all claims, damages,
        obligations, losses, liabilities, costs, debts, and expenses (including
        attorneys' fees and costs) arising from or relating to: (a) your use of
        the SMS Services; (b) your violation of this SMS Policy; (c) your
        violation of the TCPA or any other applicable laws or regulations,
        including sending messages to recipients who have not provided proper
        consent; (d) any claim by a third party related to messages you sent
        through the Platform; or (e) your breach of any representation or
        warranty contained in this SMS Policy. This indemnification obligation
        will survive termination of this SMS Policy and your use of the SMS
        Services.
      </Paragraph>

      <SubTitle>10. MODIFICATIONS AND TERMINATION</SubTitle>
      <Paragraph>
        <Strong>10.1 Changes to SMS Policy:</Strong> SkipGive reserves the right
        to modify this SMS Policy at any time in its sole discretion. We will
        provide notice of material changes by posting the updated SMS Policy on
        the Platform and updating the "Effective Date" at the top of this
        document. We may also notify you via text message or email. Your
        continued use of the SMS Services after such changes constitutes your
        acceptance of the modified SMS Policy.
      </Paragraph>
      <Paragraph>
        <Strong>10.2 Termination of SMS Services:</Strong> SkipGive reserves the
        right to terminate, suspend, or modify the SMS Services at any time, with
        or without notice, for any reason, including to comply with changes in
        law or regulations, address technical or security issues, or cease
        offering the SMS Services. We may also terminate your access to the SMS
        Services if you violate this SMS Policy or for any other reason in our
        sole discretion.
      </Paragraph>

      <SubTitle>11. DISPUTE RESOLUTION AND ARBITRATION</SubTitle>
      <Paragraph>
        Any disputes arising out of or relating to this SMS Policy or the SMS
        Services shall be resolved in accordance with the dispute resolution and
        arbitration provisions set forth in our Terms of Service, which are
        incorporated herein by reference. You agree to resolve disputes through
        binding individual arbitration and waive any right to participate in
        class action proceedings, as more fully described in the Terms of
        Service.
      </Paragraph>

      <SubTitle>12. PROGRAM AVAILABILITY AND RESTRICTIONS</SubTitle>
      <Paragraph>
        The SMS Services are currently available only to residents of the United
        States with mobile phones operating on participating U.S. carrier
        networks. You may not access or use the SMS Services from outside the
        United States. You agree to comply with all applicable U.S. export
        control laws and regulations and will not transmit any content or
        software subject to such laws to prohibited destinations or persons. We
        reserve the right to expand or limit the geographic availability of the
        SMS Services at any time.
      </Paragraph>

      <SubTitle>13. GOVERNING LAW AND ENTIRE AGREEMENT</SubTitle>
      <Paragraph>
        This SMS Policy shall be governed by and construed in accordance with the
        laws of the State of Delaware, without regard to its conflict of laws
        provisions. This SMS Policy, together with our Terms of Service and
        Privacy Policy, constitutes the entire agreement between you and SkipGive
        regarding the SMS Services and supersedes all prior agreements and
        understandings. If any provision of this SMS Policy is held to be
        invalid, illegal, or unenforceable, the remaining provisions shall remain
        in full force and effect.
      </Paragraph>

      <SubTitle>14. CONTACT INFORMATION</SubTitle>
      <Paragraph>
        If you have any questions, concerns, or complaints about this SMS Policy
        or the SMS Services, please contact SkipGive at hello@skipgive.com. By
        using the SMS Services, you acknowledge that you have read, understood,
        and agree to be bound by this SMS Text Messaging Policy.
      </Paragraph>
    </Container>
  );
};
